import type { MetricKey, PublicResult } from '../types/result';
import { assignOverallScores } from '../lib/overall_score';

interface ParticipantSeed {
  id: string;
  firstName: string;
  lastName: string;
  position: string;
  height: number;
  weight: number;
  reach: number;
  sprint10: number;
  sprint20: number;
  sprint40: number;
  broadJump: number | null;
  powerIndex: number;
  throwingAccuracy: number;
  throwingPower: number;
}

const mockThrowingKeys: MetricKey[] = ['throwingAccuracy', 'throwingPower'];

function participant(seed: ParticipantSeed): PublicResult {
  const imageId = seed.id.slice(0, 6);

  return {
    externalId: seed.id,
    eventName: 'PVille26',
    eventDate: '2026-07-18',
    firstName: seed.firstName,
    lastName: seed.lastName,
    isProfessional: false,
    benchmarkLabel: null,
    athletePosition: seed.position,
    heightInches: seed.height,
    weightPounds: seed.weight,
    reachInches: seed.reach,
    sprint10Seconds: seed.sprint10,
    sprint20Seconds: seed.sprint20,
    sprint30Seconds: null,
    sprint40Seconds: seed.sprint40,
    threeConeSeconds: null,
    verticalJumpInches: null,
    proAgilitySeconds: null,
    broadJumpInches: seed.broadJump,
    powerIndex: seed.powerIndex,
    overallScore: null,
    throwingAccuracy: seed.throwingAccuracy,
    throwingPower: seed.throwingPower,
    fullSizeImageUrl: `/images/athletes/full/${imageId}.jpg`,
    thumbnailImageUrl: `/images/athletes/thumbnails/${imageId}.jpg`,
    socialCard4x5Url: `/images/social/${imageId}-feed.jpg`,
    socialCard9x16Url: `/images/social/${imageId}-story.jpg`,
    socialCard4x5DownloadUrl: `/images/social/${imageId}-feed.jpg?download=1`,
    socialCard9x16DownloadUrl: `/images/social/${imageId}-story.jpg?download=1`,
    mockMetricKeys: mockThrowingKeys,
  };
}

// Participant measurements are a representative subset of example/dataset.csv.
// Throwing values are intentionally synthetic because those columns are not in the source file yet.
const baseParticipantResults: PublicResult[] = [
  participant({
    id: '672027_Keith_Dove',
    firstName: 'Keith',
    lastName: 'Dove',
    position: 'TE',
    height: 74.25,
    weight: 250,
    reach: 99,
    sprint10: 1.865,
    sprint20: 3.014,
    sprint40: 5.096,
    broadJump: 106,
    powerIndex: 59.27,
    throwingAccuracy: 78,
    throwingPower: 48,
  }),
  participant({
    id: '672042_Breland_Hill',
    firstName: 'Breland',
    lastName: 'Hill',
    position: 'WR',
    height: 71.75,
    weight: 180,
    reach: 91.75,
    sprint10: 1.72,
    sprint20: 2.777,
    sprint40: 4.67,
    broadJump: 116,
    powerIndex: 56.83,
    throwingAccuracy: 86,
    throwingPower: 52,
  }),
  participant({
    id: '672002_Derrick_Banks',
    firstName: 'Derrick',
    lastName: 'Banks',
    position: 'LB',
    height: 72,
    weight: 228,
    reach: 93.25,
    sprint10: 1.777,
    sprint20: 2.95,
    sprint40: 5.088,
    broadJump: 96,
    powerIndex: 53.79,
    throwingAccuracy: 72,
    throwingPower: 46,
  }),
  participant({
    id: '672038_Caleb_Gaston',
    firstName: 'Caleb',
    lastName: 'Gaston',
    position: 'QB',
    height: 72.5,
    weight: 175,
    reach: 96,
    sprint10: 1.775,
    sprint20: 2.881,
    sprint40: 4.866,
    broadJump: 117,
    powerIndex: 52.98,
    throwingAccuracy: 91,
    throwingPower: 58,
  }),
  participant({
    id: '672104_Daniel_Townsend',
    firstName: 'Daniel',
    lastName: 'Townsend',
    position: 'DL',
    height: 72.5,
    weight: 215,
    reach: 95,
    sprint10: 1.823,
    sprint20: 2.984,
    sprint40: 5.075,
    broadJump: 114,
    powerIndex: 51.9,
    throwingAccuracy: 69,
    throwingPower: 49,
  }),
  participant({
    id: '672050_Trace_Lamar',
    firstName: 'Trace',
    lastName: 'Lamar',
    position: 'WR',
    height: 71.25,
    weight: 186,
    reach: 92.5,
    sprint10: 1.867,
    sprint20: 2.985,
    sprint40: 5.063,
    broadJump: 105,
    powerIndex: 50.73,
    throwingAccuracy: 80,
    throwingPower: 51,
  }),
  participant({
    id: '672075_EJ_Neal-Adams',
    firstName: 'EJ',
    lastName: 'Neal-Adams',
    position: 'RB',
    height: 67,
    weight: 185,
    reach: 86.5,
    sprint10: 1.751,
    sprint20: 2.854,
    sprint40: 4.871,
    broadJump: 101,
    powerIndex: 50.25,
    throwingAccuracy: 74,
    throwingPower: 44,
  }),
  participant({
    id: '672101_Torey_Sutton_Jr',
    firstName: 'Torey',
    lastName: 'Sutton Jr',
    position: 'LB',
    height: 70.25,
    weight: 215,
    reach: 91.5,
    sprint10: 1.929,
    sprint20: 3.085,
    sprint40: 5.202,
    broadJump: 104,
    powerIndex: 50.03,
    throwingAccuracy: 76,
    throwingPower: 50,
  }),
  participant({
    id: '672057_Aidan_Lott',
    firstName: 'Aidan',
    lastName: 'Lott',
    position: 'LB',
    height: 68.75,
    weight: 195,
    reach: 90.25,
    sprint10: 1.792,
    sprint20: 2.956,
    sprint40: 5.078,
    broadJump: 95,
    powerIndex: 47.02,
    throwingAccuracy: 82,
    throwingPower: 47,
  }),
  participant({
    id: '672023_Champ_Davis',
    firstName: 'Champ',
    lastName: 'Davis',
    position: 'QB',
    height: 68,
    weight: 150,
    reach: 87,
    sprint10: 1.75,
    sprint20: 2.861,
    sprint40: 4.835,
    broadJump: 106,
    powerIndex: 44.84,
    throwingAccuracy: 89,
    throwingPower: 56,
  }),
  participant({
    id: '672044_Mohamed_Jabateh',
    firstName: 'Mohamed',
    lastName: 'Jabateh',
    position: 'DB',
    height: 69.5,
    weight: 160,
    reach: 90.25,
    sprint10: 1.791,
    sprint20: 2.91,
    sprint40: 4.967,
    broadJump: 104,
    powerIndex: 42.27,
    throwingAccuracy: 77,
    throwingPower: 43,
  }),
  participant({
    id: '672061_Bryce_Major',
    firstName: 'Bryce',
    lastName: 'Major',
    position: 'DB',
    height: 69.25,
    weight: 140,
    reach: 89.75,
    sprint10: 1.701,
    sprint20: 2.843,
    sprint40: 4.891,
    broadJump: 111,
    powerIndex: 41.69,
    throwingAccuracy: 84,
    throwingPower: 45,
  }),
  participant({
    id: '672112_Darius_Wynn',
    firstName: 'Darius',
    lastName: 'Wynn',
    position: 'TE',
    height: 72.25,
    weight: 190,
    reach: 96,
    sprint10: 1.935,
    sprint20: 3.219,
    sprint40: 5.513,
    broadJump: 90,
    powerIndex: 37.01,
    throwingAccuracy: 68,
    throwingPower: 48,
  }),
  participant({
    id: '672024_Damont_Davis',
    firstName: 'Damont',
    lastName: 'Davis',
    position: 'WR',
    height: 74,
    weight: 165,
    reach: 98.25,
    sprint10: 1.846,
    sprint20: 2.999,
    sprint40: 5.112,
    broadJump: 107,
    powerIndex: 36.71,
    throwingAccuracy: 79,
    throwingPower: 54,
  }),
  participant({
    id: '672084_Isaac_Ramirez_Rosales',
    firstName: 'Isaac',
    lastName: 'Ramirez Rosales',
    position: 'OL',
    height: 66,
    weight: 189,
    reach: 85.75,
    sprint10: 2.11,
    sprint20: 3.483,
    sprint40: 6.153,
    broadJump: 90,
    powerIndex: 35.35,
    throwingAccuracy: 64,
    throwingPower: 41,
  }),
  participant({
    id: '672049_Kingston_Kuhlman',
    firstName: 'Kingston',
    lastName: 'Kuhlman',
    position: 'WR',
    height: 69.5,
    weight: 148,
    reach: 89.75,
    sprint10: 1.905,
    sprint20: 3.06,
    sprint40: 5.202,
    broadJump: null,
    powerIndex: 28.86,
    throwingAccuracy: 73,
    throwingPower: 46,
  }),
];

function professional(
  id: string,
  label: string,
  sprint40: number,
  threeCone: number,
  broadJump: number,
  accuracy: number,
  power: number,
): PublicResult {
  return {
    externalId: id,
    eventName: 'Synthetic professional benchmark',
    eventDate: '2026-07-18',
    firstName: 'Synthetic',
    lastName: 'Benchmark',
    isProfessional: true,
    benchmarkLabel: label,
    athletePosition: null,
    heightInches: 73,
    weightPounds: null,
    reachInches: null,
    sprint10Seconds: null,
    sprint20Seconds: null,
    sprint30Seconds: null,
    sprint40Seconds: sprint40,
    threeConeSeconds: threeCone,
    verticalJumpInches: null,
    proAgilitySeconds: null,
    broadJumpInches: broadJump,
    powerIndex: null,
    overallScore: null,
    throwingAccuracy: accuracy,
    throwingPower: power,
    fullSizeImageUrl: null,
    thumbnailImageUrl: null,
    socialCard4x5Url: null,
    socialCard9x16Url: null,
    socialCard4x5DownloadUrl: null,
    socialCard9x16DownloadUrl: null,
    mockMetricKeys: [
      'sprint40Seconds',
      'broadJumpInches',
      'threeConeSeconds',
      'throwingAccuracy',
      'throwingPower',
    ],
  };
}

// These illustrative records are synthetic and are never included in participant rankings.
const baseProfessionalResults: PublicResult[] = [
  professional('PRO_SYNTHETIC_1', 'Synthetic pro skill benchmark', 4.43, 6.92, 126, 94, 64),
  professional('PRO_SYNTHETIC_2', 'Synthetic pro hybrid benchmark', 4.57, 7.08, 120, 90, 61),
  professional('PRO_SYNTHETIC_3', 'Synthetic pro power benchmark', 4.71, 7.24, 116, 87, 59),
];

export const mockResults: PublicResult[] = assignOverallScores([
  ...baseParticipantResults,
  ...baseProfessionalResults,
]);

export const participantResults: PublicResult[] = mockResults.filter(
  (result) => !result.isProfessional,
);

export const professionalResults: PublicResult[] = mockResults.filter(
  (result) => result.isProfessional,
);
